import React, { useState, useRef, useEffect } from 'react';
import { ConnectComponentsProvider, ConnectAccountOnboarding } from '@stripe/react-connect-js';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Landmark, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export interface StripeOnboardingStepProps {
  onAccountSessionRequest: () => Promise<{ clientSecret: string; accountId: string } | null>;
  onComplete: () => Promise<void>;
  onSkip?: () => Promise<void>;
}

export const StripeOnboardingStep: React.FC<StripeOnboardingStepProps> = ({
  onAccountSessionRequest,
  onComplete,
  onSkip,
}) => {
  const { toast } = useToast();
  const [connectInstance, setConnectInstance] = useState<ReturnType<typeof loadConnectAndInitialize> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const onAccountSessionRequestRef = useRef(onAccountSessionRequest);
  onAccountSessionRequestRef.current = onAccountSessionRequest;

  useEffect(() => {
    const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) {
      setLoadError('Payment configuration is unavailable. You can complete this later from Settings.');
      return;
    }

    const fetchClientSecret = async () => {
      const result = await onAccountSessionRequestRef.current();
      if (!result?.clientSecret) {
        throw new Error('Could not start Stripe setup');
      }
      return result.clientSecret;
    };

    setLoadError(null);
    const instance = loadConnectAndInitialize({
      publishableKey,
      fetchClientSecret,
      appearance: {
        overlays: 'dialog',
        variables: {
          colorPrimary: '#2A9D8F',
          colorBackground: '#ffffff',
          colorText: '#111827',
          borderRadius: '8px',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSizeBase: '14px',
        },
      },
    });
    setConnectInstance(instance);
  }, []);

  const handleExit = async () => {
    setIsCompleting(true);
    try {
      await onComplete();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setIsCompleting(false);
    }
  };

  const handleSkip = async () => {
    if (!onSkip) return;
    setIsSkipping(true);
    try {
      await onSkip();
      toast({
        title: 'Skipped',
        description: 'You can complete Stripe setup later from Settings.',
        variant: 'default',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setIsSkipping(false);
    }
  };

  if (loadError) {
    return (
      <div className="mx-auto max-w-lg space-y-4 py-8">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50">
            <AlertCircle className="h-7 w-7 text-red-500" />
          </div>
          <p className="text-sm text-gray-600">{loadError}</p>
          {onSkip && (
            <Button
              onClick={handleSkip}
              disabled={isSkipping}
              variant="outline"
              className="border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              {isSkipping ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continue to dashboard'}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!connectInstance) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-teal" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-teal/10">
          <Landmark className="h-7 w-7 text-brand-teal" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Set Up Payment Receiving</h2>
        <p className="mt-2 text-sm text-gray-500">
          Complete Stripe Connect so your clinic can receive payments. This step is secure and can be finished later from Settings.
        </p>
      </div>

      <Card className="border-gray-200 bg-white shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-gray-900">Stripe Connect</CardTitle>
          <CardDescription className="text-gray-500">
            Enter your business and banking details in the form below.
          </CardDescription>
        </CardHeader>
        <CardContent className="min-h-[320px]">
          <ConnectComponentsProvider connectInstance={connectInstance}>
            <ConnectAccountOnboarding
              onExit={handleExit}
              onStepChange={() => {}}
              onLoadError={({ error }) => {
                setLoadError(error?.message ?? 'Failed to load Stripe form');
                toast({
                  title: 'Error',
                  description: error?.message ?? 'Failed to load form',
                  variant: 'destructive',
                });
              }}
            />
          </ConnectComponentsProvider>
        </CardContent>
      </Card>

      {onSkip && (
        <div className="text-center">
          <button
            type="button"
            onClick={handleSkip}
            disabled={isSkipping || isCompleting}
            className="text-sm text-gray-500 underline underline-offset-2 hover:text-gray-700"
          >
            {isSkipping ? 'Skipping...' : 'Skip for now — complete later in Settings'}
          </button>
        </div>
      )}
    </div>
  );
};

export default StripeOnboardingStep;
